import React, { Dispatch, ReactNode, SetStateAction } from 'react';

import { Controller } from 'react-hook-form';

import moment from 'moment';

import useDineInOrderHook from './dineInOrder-hook';

import { ButtonTypes, ICartItem, IDineInLocation } from '../../../../../../../../Interfaces/componentTypes';

import { IBusinessProfile } from '../../../../../../../../Interfaces/responseTypes';

import { VisitorPageContents } from '../../../../../visitorPage';

import useLanguagesHooks from '../../../../../../../../Components/Languages/setLanguage-hooks';

import { CartIcon, DineinIcon, LocationIcon, PencilIcon } from '../../../../../../../../Components/IconComponents';

import Button from '../../../../../../../../Components/Button/button';

import CustomDialog from '../../../../../../../../Components/Dialog/customDialog';

import AuthInput from '../../../../../../../../Components/Inputs/AuthInput/authInput';

import TelInput from '../../../../../../../../Components/Inputs/TelInput/telInput';

import CustomTimePicker from '../../../../../../../../Components/Inputs/TimePicker/timePicker';

import NoteDialog from '../NoteDialog/noteDialog';

import OrderPaymentSection from '../../OrderPayment/orderPaymentSection';

import OrderScheduleNotices from '../../OrderScheduleNotices/orderScheduleNotices';

import styles from './dineInOrder.module.scss';



export interface IDineInOrderProps {

    companyId: number,

    company_name: string,

    company_logo: string,

    company_contacts?: string[],

    cart: ICartItem[],

    currencySymbol: string,

    dineInPrice: number,

    totalPrice: number,

    serviceFee?: number,

    pos_terminal?: boolean | null,

    totalItemCount: number,

    business_profile?: IBusinessProfile | null,

    start_time?: string | null,

    end_time?: string | null,

    dineInLocation: IDineInLocation,

    onOpenMainCartDialog: () => void,

    setDialog: Dispatch<SetStateAction<ReactNode>>,

    setMainCart: Dispatch<SetStateAction<[] | ICartItem[]>>,

    onCloseDialog: () => void,

    setContentType: Dispatch<SetStateAction<VisitorPageContents>>,

}



const DineInOrderDialog: React.FC<IDineInOrderProps> = (props) => {

    const { currencySymbol, onOpenMainCartDialog, totalItemCount, totalPrice, dineInLocation, serviceFee, business_profile, start_time, end_time } = props;

    const {

        open,

        errors,

        control,

        dialogRef,

        newOrderLoading,

        isScheduleRequired,

        onOrder,

        register,

        getValues,

        watch,

        handleNote,

        onOpenNoteDialog,

        onCloseNoteDialog,

        normalizedTotalPrice,

    } = useDineInOrderHook(props);

    const { t } = useLanguagesHooks();



    const locationLabel = `${t(`My_Business.My_Services.Dine_In.Unit_Types.${dineInLocation.location_type}`)} #${dineInLocation.location_number}`;

    const notes = getValues('notes');

    const notePreview = notes && notes.length > 40 ? `${notes.slice(0, 40)}...` : notes;

    const selectedDate = watch('date');



    return (
        <div className={styles.container}>
            <div className={styles.topSection}>
                <OrderScheduleNotices profile={business_profile} selectedDate={selectedDate} />
                <div className={styles.header}>
                    <DineinIcon className={styles.plateIcon} />
                    <p>{t('Visitor.Dine_In_Order.Header')}</p>
                </div>
                <div className={styles.locationRow}>
                    <LocationIcon width={16} height={16} color="var(--primary-button)" />
                    <span className={styles.locationText}>{locationLabel}</span>
                </div>
            </div>

            <div ref={dialogRef} className={styles.scrollContent}>
                <div className={styles.body}>
                    <div className={styles.formSection}>
                        <div className={styles.form}>
                            <TelInput
                                name='phone_number'
                                control={control}
                                label={t('Input_Labels.Phone')}
                                showTextError={false}
                                style={styles.tellInput}
                                error={errors.phone_number}
                            />
                            <div className={styles.dateTimeBox}>
                                <p className={styles.label}>
                                    {t(isScheduleRequired ? 'Visitor.Delivery.Time_Required' : 'Visitor.Delivery.Time')}
                                    {isScheduleRequired && (errors.time || errors.date)
                                        && <span style={{ color: '#b00020' }}> *</span>
                                    }
                                </p>
                                <div className={styles.dateTimeInputRow}>
                                    <AuthInput
                                        register={register}
                                        registerName='date'
                                        type='date'
                                        showTextError={false}
                                        style={styles.datePicker}
                                        inputBoxStyles={styles.datePickerInput}
                                        defaultValue={selectedDate}
                                        minDate={moment().format('YYYY-MM-DD')}
                                        required={isScheduleRequired}
                                        error={errors.date}
                                    />
                                    <Controller
                                        name="time"
                                        control={control}
                                        rules={{ required: isScheduleRequired }}
                                        render={({ field, fieldState }) => (
                                            <CustomTimePicker
                                                value={field.value ?? null}
                                                onChange={field.onChange}
                                                style={styles.timePicker}
                                                showTextError={false}
                                                placeholder=""
                                                error={fieldState.error ?? errors.time}
                                                startTime={start_time}
                                                endTime={end_time}
                                                selectedDate={selectedDate}
                                                businessProfile={business_profile}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                            <div className={styles.noteContainer}>
                                <div className={styles.noteLabel}>
                                    {t('Visitor.Delivery.Notes')}
                                </div>
                                <div className={styles.noteBox} onClick={onOpenNoteDialog}>
                                    <div className={styles.noteText}>
                                        {notePreview ? (
                                            <p>{notePreview}</p>
                                        ) : (
                                            <p className={styles.notePlaceholder}>{t('Visitor.Add_Note')}</p>
                                        )}
                                    </div>
                                    <PencilIcon color='var(--input-label)' />
                                </div>
                            </div>
                        </div>

                        <OrderPaymentSection pos_terminal={props.pos_terminal} control={control} />
                    </div>
                </div>

                <div className={styles.totalBox}>
                    <div className={styles.totalInfoBox}>
                        <div className={styles.totaInfoRow}>
                            <p className={styles.totalInfoTitle}>
                                {t('Visitor.Delivery.Order')}
                            </p>
                            <div className={styles.totalItemsInfoRow}>
                                <p className={styles.totalItemsCount}>
                                    {`${totalItemCount} ${totalItemCount > 1 ? t('Visitor.Delivery.Items') : t('Visitor.Delivery.Item')}`}
                                </p>
                                <p className={styles.totalCost}>
                                    {`${parseInt(String(totalPrice))}${currencySymbol}`}
                                </p>
                            </div>
                        </div>
                        {serviceFee != null && serviceFee > 0 && (
                            <div className={styles.totaInfoRow}>
                                <p className={styles.totalInfoTitle}>
                                    {t('Visitor.Service_Fee')}
                                </p>
                                <p className={styles.totalCost}>
                                    {`${serviceFee}%`}
                                </p>
                            </div>
                        )}
                    </div>
                    <div className={styles.totaInfoRow}>
                        <p className={styles.totalDeliveryTitle}>
                            {t('Visitor.Delivery.Total')}
                        </p>
                        <p className={styles.totalDeliveryCost}>
                            {`${parseInt(String(normalizedTotalPrice))} ${currencySymbol}`}
                        </p>
                    </div>
                </div>
            </div>

            <div className={styles.buttonsRow}>
                <Button
                    buttonType={ButtonTypes.Primery}
                    title={t('Visitor.Cart')}
                    leftIcon={<CartIcon width={20} height={20} />}
                    buttonStyle={styles.cartButton}
                    buttonTitleStyle={styles.buttontitle}
                    onClick={onOpenMainCartDialog}
                />
                <Button
                    buttonType={ButtonTypes.Primery}
                    title={t('Visitor.Dine_In_Order.Order_Button')}
                    buttonStyle={styles.orderButton}
                    buttonTitleStyle={styles.buttontitle}
                    isLoading={newOrderLoading}
                    onClick={onOrder}
                />
            </div>

            <CustomDialog
                open={open}
                onClose={onCloseNoteDialog}
                children={
                    <NoteDialog
                        note={getValues('notes')}
                        onHandleNote={handleNote}
                    />
                }
            />
        </div>
    );

};



export default DineInOrderDialog;

